import { isDefined } from '@sgme/fp';
import type { CompetitionParameterFeedback } from 'components/shared/WithErrorTooltip/model';
import type { PriceEnhancement } from 'state/quotes/trades/trades.model';
import { getIsUserInternal, getUserFrontRights } from 'state/selectors';
import type { AppState } from 'state/state.model';
import { LoadingStatus } from 'utils/status';

import type { Feedback, FeedbackAck, MissedFeedbackCompetitionParameter } from './feedback/model';

export const getFeedback = (state: AppState, tabId: string): Feedback | undefined =>
  state.missedDealFeedback.feedback[tabId];

const getAdditionalInformation = (state: AppState, tabId: string) => {
  return getFeedback(state, tabId)?.additionalInformation;
};
export const getCompetitionParametersReferential = (
  state: AppState,
  tabId: string,
): MissedFeedbackCompetitionParameter[] | undefined => {
  const feedback = getAdditionalInformation(state, tabId);
  return feedback?.competitionParametersReferential;
};
export const getFeedbackComment = (state: AppState, tabId: string): string | undefined => {
  const feedback = getFeedback(state, tabId);
  return feedback?.comment;
};
export const getFeedbackIsClientTraded = (state: AppState, tabId: string): boolean => {
  return getAdditionalInformation(state, tabId)?.isClientTraded ?? true;
};
export const getFeedbackIsClientNotTraded = (state: AppState, tabId: string): boolean => {
  return !getFeedbackIsClientTraded(state, tabId);
};
export const getFeedbackSgPosition = (state: AppState, tabId: string): number | undefined => {
  return getAdditionalInformation(state, tabId)?.sgPosition;
};
export const getFeedbackSgPositionIsValid = (state: AppState, tabId: string): boolean => {
  const sgPosition = getFeedbackSgPosition(state, tabId);
  const numberOfIssuers = getFeedbackNumberOfIssuers(state, tabId);
  return !(
    (numberOfIssuers !== undefined && (sgPosition === undefined || sgPosition > numberOfIssuers)) ||
    (sgPosition !== undefined && sgPosition <= 0)
  );
};
export const getFeedbackNumberOfIssuers = (state: AppState, tabId: string): number | undefined => {
  return getAdditionalInformation(state, tabId)?.numberOfIssuers;
};

export const getFeedbackNumberOfIssuersIsValid = (state: AppState, tabId: string): boolean => {
  const sgPosition = getFeedbackSgPosition(state, tabId);
  const numberOfIssuers = getFeedbackNumberOfIssuers(state, tabId);
  return !(
    (sgPosition !== undefined && (numberOfIssuers === undefined || sgPosition > numberOfIssuers)) ||
    (numberOfIssuers !== undefined && numberOfIssuers <= 0)
  );
};

export const getFeedbackIsSgPositionning = (state: AppState, tabId: string): boolean => {
  return getAdditionalInformation(state, tabId)?.isSgPositionning ?? false;
};
export const getFeedbackIsPriceEnhanced = (state: AppState, tabId: string): boolean => {
  return !!getAdditionalInformation(state, tabId)?.priceEnhancement;
};

export const getUserFeedbackPriceEnhancement = (state: AppState, tabId: string): PriceEnhancement | undefined => {
  return getAdditionalInformation(state, tabId)?.priceEnhancement;
};

export const getCompetitionParametersFeedbacks = (
  state: AppState,
  tabId: string,
): CompetitionParameterFeedback[] | undefined => {
  return state.missedDealFeedback.competitionParametersFeedback[tabId];
};

export const getFeedbackPricerId = (state: AppState, tabId: string): string | undefined => {
  return getFeedback(state, tabId)?.pricerId;
};

export const isFeedbackInitialized = (state: AppState, tabId: string): boolean => {
  return isDefined(getFeedback(state, tabId));
};

function getIsUserAuthorized(state: AppState): boolean {
  const userIsInternal = getIsUserInternal(state);
  const hasFeedbackRights = getUserFrontRights(state).feedbackEnabled;
  const hasTradeRights = getUserFrontRights(state).tradeEnabled;

  return userIsInternal && (hasTradeRights || hasFeedbackRights);
}
function isFeedbackWithAdditionalInformationCanBeSubmitted(state: AppState, tabId: string): boolean {
  const parametersFeedback = getCompetitionParametersFeedbacks(state, tabId);

  const sgPositionIsValid = getFeedbackSgPositionIsValid(state, tabId);
  const numberOfIssuersIsValid = getFeedbackNumberOfIssuersIsValid(state, tabId);

  return (
    parametersFeedback !== undefined &&
    parametersFeedback.length !== 0 &&
    parametersFeedback.every(p => p.label !== undefined && p.bestOfferValue !== undefined) &&
    sgPositionIsValid &&
    numberOfIssuersIsValid
  );
}

export const getFeedbackCanBeSubmitted = (state: AppState, tabId: string): boolean => {
  const withAdditionalInfo = getIsWithAdditionalInformation(state, tabId);
  const isUserAuthorized = getIsUserAuthorized(state);
  if (withAdditionalInfo === true) {
    return isUserAuthorized && isFeedbackWithAdditionalInformationCanBeSubmitted(state, tabId);
  }
  return isUserAuthorized && withAdditionalInfo === false;
};
export const getIsWithAdditionalInformation = (state: AppState, tabId: string): boolean | undefined => {
  const feedback = getFeedback(state, tabId);
  return feedback?.withAdditionalInfo;
};

export const getSubmissionStatus = (state: AppState, tabId: string) => {
  return getFeedback(state, tabId)?.submissionStatus;
};
export const getReferentialStatus = (state: AppState, tabId: string) => {
  return getAdditionalInformation(state, tabId)?.referentialStatus;
};
export const isFeedbackSubmissionRunning = (state: AppState, tabId: string): boolean => {
  return getSubmissionStatus(state, tabId) === 'loading';
};

export const getFeedbackAck = (state: AppState, tabId: string): FeedbackAck | undefined => {
  const feedback = getFeedback(state, tabId);
  return feedback?.ack;
};

export const getFeedbackSubmissionRequestStartDateInMilliseconds = (state: AppState, tabId: string) => {
  const feedback = getFeedback(state, tabId);
  return feedback?.submissionStartDateInMilliseconds;
};

export const getFeedbackSubmissionEstimatedTimeInMilliseconds = (state: AppState, tabId: string) => {
  const feedback = getFeedback(state, tabId);
  return feedback?.submissionEstimatedTimeInMilliseconds;
};

export function getTechnicalError(state: AppState, tabId: string) {
  const feedback = getFeedback(state, tabId);
  return feedback?.technicalError;
}

export function getFeedbackStatus(state: AppState, tabId: string) {
  const withAdditionalInfo = getIsWithAdditionalInformation(state, tabId);
  if (withAdditionalInfo === true) {
    return getWithAdditionalInfoStatus(state, tabId);
  } else if (withAdditionalInfo === false) {
    return getWithoutAdditionalInfoStatus(state, tabId);
  } else {
    return 'Idle' as const;
  }
}

function getWithoutAdditionalInfoStatus(state: AppState, tabId: string) {
  const submissionStatus = mapSubmissionStatus(getSubmissionStatus(state, tabId));
  return submissionStatus ?? ('ReadyForSubmission' as const);
}
function getWithAdditionalInfoStatus(state: AppState, tabId: string) {
  const submissionStatus = mapSubmissionStatus(getSubmissionStatus(state, tabId));
  return (
    submissionStatus ??
    getSubmissionRequestedStatus(state, tabId) ??
    mapReferentialStatus(getReferentialStatus(state, tabId)) ??
    ('ReadyForSubmission' as const)
  );
}
function getSubmissionRequestedStatus(state: AppState, tabId: string) {
  const isSubmissionRequested = getFeedback(state, tabId)?.submissionRequested;
  return isSubmissionRequested === true ? ('SubmissionRequested' as const) : undefined;
}
function mapSubmissionStatus(status?: LoadingStatus) {
  switch (status) {
    case 'loading':
      return 'SubmissionPending' as const;
    case 'error':
      return 'SubmissionFailed' as const;
    case 'success':
      return 'Submitted' as const;
  }
}

function mapReferentialStatus(status?: LoadingStatus) {
  switch (status) {
    case 'loading':
      return 'Loading' as const;
    case 'error':
      return 'LoadingFailed' as const;
  }
}
